import React, {useMemo, useState} from "react";
import {
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    Tooltip,
    Chip,
    Divider,
} from "@mui/material";

/* ===== IBAN Helpers ===== */

// Entfernt Leerzeichen/Striche und uppercased
const normalizeIban = (iban = "") => iban.replace(/[\s-]/g, "").toUpperCase();

// IBAN hübsch gruppieren (4er-Gruppen)
const prettifyIban = (iban = "") =>
    normalizeIban(iban).replace(/(.{4})/g, "$1 ").trim();

// Mod-97 Check (ISO 13616)
const isValidIban = (raw = "") => {
    const iban = normalizeIban(raw);
    if (!/^[A-Z0-9]{15,34}$/.test(iban)) return false;
    const rearranged = iban.slice(4) + iban.slice(0, 4);
    const converted = rearranged
        .split("")
        .map((ch) => (ch >= "A" && ch <= "Z" ? ch.charCodeAt(0) - 55 : ch))
        .join("");
    // Große Zahl modulo 97: in Blöcken rechnen
    let remainder = 0;
    for (let i = 0; i < converted.length; i += 7) {
        const block = String(remainder) + converted.substring(i, i + 7);
        remainder = Number(block) % 97;
    }
    return remainder === 1;
};

const isValidBic = (raw = "") =>
    /^[A-Za-z]{4}[A-Za-z]{2}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/.test(raw);

const cryptoRandomId = () =>
    typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);

/* ===== Component ===== */

export default function BankModule({
    onBankChange,
    bankConnections,
    showOutgoingCheckbox = true,
    validationErrors = {},
}) {
    const [openDialog, setOpenDialog] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const emptyBank = () => ({
        id: "",
        iban: "",
        bic: "",
        bankName: "",
    });

    const [currentBank, setCurrentBank] = useState(emptyBank());

    const selectedIndex = useMemo(
        () => (bankConnections ? bankConnections.findIndex((b) => b.forOutgoingInvoice) : -1),
        [bankConnections]
    );

    /* ===== Dialog open/close ===== */
    const handleDialogOpen = () => {
        setCurrentBank(emptyBank());
        setEditingIndex(null);
        setFieldErrors({});
        setOpenDialog(true);
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
        setCurrentBank(emptyBank());
        setEditingIndex(null);
        setFieldErrors({});
    };

    /* ===== CRUD ===== */
    const handleSave = () => {
        const normalizedIban = normalizeIban(currentBank.iban);
        const normalizedBic = (currentBank.bic || "").replace(/\s+/g, "").toUpperCase();
        const nextErrors = {};
        if (!normalizedIban) {
            nextErrors.iban = "Bitte eine IBAN angeben.";
        } else if (!isValidIban(normalizedIban)) {
            nextErrors.iban = "Ungültige IBAN.";
        }
        if (!normalizedBic) {
            nextErrors.bic = "Bitte BIC angeben.";
        } else if (!isValidBic(normalizedBic)) {
            nextErrors.bic = "Ungültiger BIC.";
        }
        if (!currentBank.bankName?.trim()) {
            nextErrors.bankName = "Bitte Bankname angeben.";
        }
        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            return;
        }

        const withId = {
            ...currentBank,
            id: currentBank.id || cryptoRandomId(),
            iban: normalizedIban,
            bic: normalizedBic,
        };

        // Wenn Standard → alle anderen auf false
        const updated = [...bankConnections].map((b) =>
            withId.forOutgoingInvoice ? {...b, forOutgoingInvoice: false} : b
        );

        if (editingIndex != null) {
            updated[editingIndex] = withId;
        } else {
            updated.push(withId);
        }

        onBankChange(updated);
        handleDialogClose();
    };

    const handleEdit = (index) => {
        const b = bankConnections[index];
        setCurrentBank(b || emptyBank());
        setEditingIndex(index);
        setFieldErrors(validationErrors?.[index] || {});
        setOpenDialog(true);
    };

    const handleDelete = (index) => {
        const updated = bankConnections.filter((_, i) => i !== index);
        onBankChange(updated);
    };

    const handleCheckboxChange = (index) => {
        const isSelected = bankConnections[index]?.forOutgoingInvoice;
        const updated = bankConnections.map((bank, i) => ({
            ...bank,
            forOutgoingInvoice: i === index ? !isSelected : false,
        }));
        onBankChange(updated);
    };

    const handleFieldChange = (field, value) => {
        setCurrentBank((prev) => ({ ...prev, [field]: value }));
        if (fieldErrors[field]) {
            setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    return (
        <Box sx={{mt: 3}}>
            <Box
                sx={{
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                }}
            >
                <Typography variant="h6" fontWeight={700}>
                    Bankverbindungen
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleDialogOpen}
                    sx={{bgcolor: "orange", "&:hover": {bgcolor: "#cc7a00"}, borderRadius: 2}}
                >
                    Bankverbindung hinzufügen
                </Button>
            </Box>

            <Grid container spacing={2}>
                {bankConnections?.map((bank, index) => (
                    <Grid item xs={12} sm={6} md={4} key={bank.id || index}>
                        <Card
                            variant="outlined"
                            sx={{borderRadius: 3, height: "100%", boxShadow: "0 8px 28px rgba(0,0,0,0.06)"}}
                        >
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        justifyContent: "space-between",
                                        gap: 1,
                                        mb: 0.5,
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            {bank.bankName || "—"}
                                        </Typography>
                                        {validationErrors?.[index] &&
                                            Object.keys(validationErrors[index]).length > 0 && (
                                                <Tooltip
                                                    title={Object.values(validationErrors[index]).join("; ")}
                                                >
                                                    <Chip
                                                        label="Fehler"
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                    />
                                                </Tooltip>
                                            )}
                                    </Box>
                                    {bank.forOutgoingInvoice && (
                                        <Chip label="Standard" size="small" color="success" variant="outlined"/>
                                    )}
                                </Box>
                                <Divider sx={{mb: 1}}/>
                                <Typography variant="body2">
                                    <strong>IBAN:</strong> {prettifyIban(bank.iban) || "—"}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>BIC:</strong> {bank.bic || "—"}
                                </Typography>

                                <Tooltip
                                    title={
                                        selectedIndex !== -1 && selectedIndex !== index
                                            ? "Es ist bereits eine Standardverbindung ausgewählt"
                                            : ""
                                    }
                                >
                  <span>
                      {showOutgoingCheckbox && (
                          <FormControlLabel
                              sx={{mt: 1}}
                              control={
                                  <Checkbox
                                      checked={!!bank.forOutgoingInvoice}
                                      onChange={() => handleCheckboxChange(index)}
                                      disabled={selectedIndex !== -1 && selectedIndex !== index}
                                  />
                              }
                              label="Für Ausgangsrechnungen verwenden"
                          />
                      )}
                  </span>
                                </Tooltip>
                            </CardContent>

                            <CardActions sx={{pt: 0, pb: 2, px: 2}}>
                                <Button size="small" onClick={() => handleEdit(index)}>
                                    Bearbeiten
                                </Button>
                                <Button size="small" color="error" onClick={() => handleDelete(index)}>
                                    Löschen
                                </Button>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}

                {(!bankConnections || bankConnections.length === 0) && (
                    <Grid item xs={12}>
                        <Card variant="outlined" sx={{borderRadius: 3}}>
                            <CardContent>
                                <Typography color="text.secondary">
                                    Noch keine Bankverbindung hinterlegt. Füge deine erste Verbindung hinzu.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>

            {/* Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleDialogClose}
                fullWidth
                maxWidth="sm"
                PaperProps={{sx: {borderRadius: 3}}}
            >
                <DialogTitle>
                    {editingIndex != null ? "Bankverbindung bearbeiten" : "Bankverbindung hinzufügen"}
                </DialogTitle>
                <DialogContent dividers sx={{pt: 2}}>
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: 2}}>
                        <TextField
                            label="IBAN"
                            value={currentBank.iban}
                            onChange={(e) => {
                                handleFieldChange("iban", e.target.value);
                            }}
                            fullWidth
                            required
                            error={Boolean(fieldErrors.iban)}
                            helperText={fieldErrors.iban || ""}
                            placeholder="DE89 3704 0044 0532 0130 00"
                        />
                        <TextField
                            label="BIC"
                            value={currentBank.bic}
                            onChange={(e) => handleFieldChange("bic", e.target.value)}
                            fullWidth
                            required
                            error={Boolean(fieldErrors.bic)}
                            helperText={fieldErrors.bic || ""}
                            placeholder="MARKDEF1100"
                        />
                    </Box>
                    <TextField
                        sx={{mt: 2}}
                        label="Bankname"
                        value={currentBank.bankName}
                        onChange={(e) => handleFieldChange("bankName", e.target.value)}
                        fullWidth
                        required
                        error={Boolean(fieldErrors.bankName)}
                        helperText={fieldErrors.bankName || ""}
                        placeholder="z. B. Musterbank"
                    />
                    {showOutgoingCheckbox && (
                    <FormControlLabel
                        sx={{mt: 1}}
                        control={
                            <Checkbox
                                checked={!!currentBank.forOutgoingInvoice}
                                onChange={(e) =>
                                    setCurrentBank({...currentBank, forOutgoingInvoice: e.target.checked})
                                }
                            />
                        }
                        label="Als Standard für Ausgangsrechnungen festlegen"
                    />
                    )}
                </DialogContent>
                <DialogActions sx={{p: 2}}>
                    <Button onClick={handleDialogClose}>Abbrechen</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        sx={{bgcolor: "orange", "&:hover": {bgcolor: "#cc7a00"}, borderRadius: 2}}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
